import { FC, useCallback } from 'react';
import { router } from '@inertiajs/react';

import { Button } from '@/Components/Button';
import { useToast } from '@/hook/useToast';
import { AppModalProps } from '@/types/modal.type';
import { ModalView } from '@/Components/ModalView';
import { FlickAnimation } from '@/Components/FlickAnimation';
import { FruitBlueberrySvg } from '@/Components/Icons/FruitBlueberrySvg';

type UnpublishFlowModalProps = AppModalProps<{
    flowId: number;
    flowName: string;
    reason?: 'delete' | 'archive';
}>;

export const UnpublishFlowModal: FC<UnpublishFlowModalProps> = ({
    payload,
    onClose,
}) => {
    const { showToast } = useToast();

    const onUnpublish = useCallback(() => {
        onClose();

        return new Promise<null>((resolve, reject) => {
            router.post(
                route('flows.unpublish', {
                    flowId: payload.flowId,
                }),
                {},
                {
                    onSuccess: () => {
                        showToast({
                            autoClose: 5000,
                            variant: 'success',
                            label: `Flow ${payload.flowName} has been unpublished`,
                        });

                        resolve(null);
                    },
                    onError: (errors) => {
                        Object.keys(errors).forEach((errorKey) => {
                            showToast({
                                autoClose: 5000,
                                variant: 'danger',
                                label: errors[errorKey],
                            });
                        });

                        reject(errors);
                    },
                },
            );
        });
    }, [onClose]);

    const bodyText =
        payload.reason === 'archive'
            ? `This Flow “${payload.flowName}” must be Unpublished before it can be archived.`
            : `This Flow “${payload.flowName}” must be Unpublished before it can be deleted.`;

    return (
        <ModalView
            maxWidth="sm"
            onClose={onClose}
            onSubmit={onUnpublish}
            contentContainerClassName="py-6 px-8 text-brand-ocean flex flex-col justify-center items-center">
            <div className="relative min-w-60 h-24 flex items-center justify-center">
                <FlickAnimation className="absolute z-10 bottom-5 left-11" />
                <div className="absolute bottom-3 right-16">
                    <FruitBlueberrySvg bgFill="white" className="w-14 h-14" />
                </div>
            </div>
            <div className="w-full text-body-text-15 text-left text-brand-ocean mb-4 mt-2">
                {bodyText}
            </div>
            <div className="mt-2 w-full flex flex-col items-center justify-center gap-2">
                <Button
                    className="w-full h-9"
                    variant="primary"
                    onClick={onUnpublish}>
                    Unpublish
                </Button>
                <Button
                    className="w-full h-9"
                    variant="default"
                    onClick={onClose}>
                    Cancel
                </Button>
            </div>
        </ModalView>
    );
};
